import { useState, useRef, useEffect } from "react";
import { List as HamburgerIcon } from "@phosphor-icons/react";
import RecallControl from "@/components/RecallControl";
import { useTabsOff, setTabsOff, useHiddenTabList, openHiddenTab } from "@/lib/tabToggle";

// Shared silo-page top-left navigation: the MOBILE hamburger pill (with its
// dropdown) + the DESKTOP Recall control. Used by BOTH the Public Q&A page and
// the private AI Assistant page, so any visual/behavioral change here updates
// both pages in lockstep (Randy, Jun 2026).
//
// Jul 2026 (Randy) — "Home" and "Dark mode" were REMOVED from this nav (both the
// mobile hamburger dropdown AND the desktop buttons) now that both live inside
// the IdentityPill dropdown. The hamburger now only carries Recall + any
// page-specific items (Clear / Export). onHome/homeTestid are still accepted for
// call-site compatibility but no longer rendered.
//   items — dropdown items beyond Recall:
//           [{ label, icon, onClick, testid, show }] (show !== false renders)
export function SiloHeaderNav({ onHome, items = [], footerItems = [], testidPrefix = "mobile-menu", homeTestid = "silo-nav-home", recall = null, tabsToggle = false }) {
  const [open, setOpen] = useState(false);
  // Jul 2026 (Randy) — "Turn off tabs": hides every edge tab/bump and lists
  // them as a selector under the toggle instead. Persisted per device.
  const tabsOff = useTabsOff();
  const hiddenTabs = useHiddenTabList();
  // When Recall is tapped, collapse the dropdown to show ONLY the Recall row for
  // the 5s rotation (Randy). Closing the menu (tap-away or toggle) resets it, so
  // the next open shows all options again.
  const [recallFocus, setRecallFocus] = useState(false);
  // Jun 2026 (Randy) — tapping ANYWHERE outside the menu closes it. A plain
  // backdrop isn't enough: the header's backdrop-blur creates a containing block
  // that traps `position:fixed`, so the overlay can't cover the whole screen. A
  // document-level pointerdown listener works regardless of stacking context.
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false); setRecallFocus(false);
      }
    };
    document.addEventListener("pointerdown", onDoc, true);
    return () => document.removeEventListener("pointerdown", onDoc, true);
  }, [open]);

  const menu = [...items].filter((i) => i.show !== false);
  // Jul 25, 2026 (Randy) — footer rows pinned to the BOTTOM of the dropdown
  // (e.g. Report / Tip / Subscribe on the Public Q&A). Rendered on all
  // viewports. Each: { label, onClick, testid, show }.
  const footer = [...footerItems].filter((i) => i.show !== false);
  // With Home/Dark gone, the hamburger can be empty — only render it when it
  // would actually carry something (Recall, a page item, or the tabs toggle).
  const hasHamburger = tabsToggle || !!recall?.enabled || menu.length > 0 || footer.length > 0;

  return (
    <>
      {/* Hamburger pill — mobile-only normally; ALL viewports when it carries
          the tabs toggle (Randy: the toggle must be reachable on desktop too). */}
      {hasHamburger && (
        <div className={`relative shrink-0 ${tabsToggle ? "" : "sm:hidden"}`} ref={wrapRef}>
          <button
            onClick={() => { setOpen((v) => !v); setRecallFocus(false); }}
            data-testid={`${testidPrefix}-toggle`}
            aria-label="Menu"
            className="tpl-muted hover:tpl-accent flex items-center justify-center w-9 h-9 rounded-md border tpl-border tpl-surface"
          >
            <HamburgerIcon size={16} weight="bold" />
          </button>
          {open && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setRecallFocus(false); }} data-testid={`${testidPrefix}-backdrop`} />
              <div className="absolute left-0 top-full mt-1 z-50 w-48 tpl-surface border tpl-border rounded-md shadow-lg py-1" data-testid={testidPrefix}>
                {/* Recall + page items stay MOBILE-only (desktop already shows them as buttons). */}
                <div className={tabsToggle ? "sm:hidden" : ""}>
                  {recall?.enabled && <RecallControl {...recall} variant="mobile" onRecallTap={() => setRecallFocus(true)} />}
                  {!recallFocus && menu.map((it) => (
                    <button
                      key={it.testid}
                      onClick={() => { setOpen(false); setRecallFocus(false); it.onClick && it.onClick(); }}
                      data-testid={it.testid}
                      className="w-full text-left px-3 py-2 text-[13px] tpl-muted hover:tpl-accent hover:bg-black/5 flex items-center gap-2"
                    >
                      {it.icon} {it.label}
                    </button>
                  ))}
                </div>
                {tabsToggle && !recallFocus && (
                  <div className="border-t tpl-border mt-1 pt-1">
                    <button
                      onClick={() => setTabsOff(!tabsOff)}
                      data-testid="tabs-off-toggle"
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-[13px] tpl-muted hover:tpl-accent hover:bg-black/5"
                    >
                      <span>Turn off tabs</span>
                      <span className={`relative inline-flex h-4 w-8 shrink-0 rounded-full transition-colors ${tabsOff ? "bg-[#4A5D4E]" : "bg-black/20"}`}>
                        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${tabsOff ? "left-[18px]" : "left-0.5"}`} />
                      </span>
                    </button>
                    {tabsOff && hiddenTabs.length > 0 && (
                      <div data-testid="tabs-off-selector">
                        <div className="px-3 pt-1 pb-0.5 text-[9px] uppercase tracking-[0.2em] tpl-muted opacity-70">Tabs</div>
                        {hiddenTabs.map((t) => (
                          <button
                            key={t.key}
                            onClick={() => { setOpen(false); setRecallFocus(false); openHiddenTab(t.key); }}
                            data-testid={`tabs-menu-${t.key}`}
                            className="w-full text-left px-3 py-2 text-[13px] tpl-muted hover:tpl-accent hover:bg-black/5"
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {footer.length > 0 && !recallFocus && (
                  <div className="border-t tpl-border mt-1 pt-1" data-testid={`${testidPrefix}-footer`}>
                    {footer.map((it) => (
                      <button
                        key={it.testid}
                        onClick={() => { setOpen(false); setRecallFocus(false); it.onClick && it.onClick(); }}
                        data-testid={it.testid}
                        className="w-full text-left px-3 py-2 text-[13px] tpl-muted hover:tpl-accent hover:bg-black/5 truncate"
                      >
                        {it.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {/* DESKTOP: Recall (top-left) — same as the top of the mobile menu. */}
      {recall?.enabled && <RecallControl {...recall} variant="desktop" />}
    </>
  );
}

export default SiloHeaderNav;
